import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["editor", "input", "fileInput", "multiFileInput", "wrapper", "fontInput", "blockMenu", "insertTrigger", "saveStatus", "textColorSwatch", "bgColorSwatch", "newsletterBgSwatch", "bgColorInput", "surroundings", "toolbar", "toolbarSentinel"]
  static values = { autosaveUrl: String }

  connect() {
    this.editorTarget.innerHTML = this.inputTarget.value || ""
    // Ensure empty contenteditable has a focusable node
    if (!this.editorTarget.innerHTML.trim()) {
      this.editorTarget.innerHTML = "<p><br></p>"
    }
    // Block-builder mode (newsletter) shows drag/delete/insert overlays and a
    // block menu. The simple rich-text editor (admin welcome text) has none of
    // those targets — skip the overlays so they aren't clipped by the editor's
    // overflow-hidden frame, and let Backspace/Enter behave natively.
    this._blockMode = this.hasInsertTriggerTarget || this.hasBlockMenuTarget

    this._setupImageResize()
    this._setupActiveBlockTracking()
    this._setupToolbarStuck()
    if (this._blockMode) {
      this._setupBlockMenu()
      this._setupStaticInsertTrigger()
      this._setupBlockClickSelection()
      this._setupDeleteBlockBtn()
      this._setupDragHandle()
      this._setupBlockKeyboardProtect()
      this._setupBlockMenuDismiss()
      this._setupEnterKey()
    }
    this._refreshDraggables()
    this.form = this.element.closest("form")
    if (this.form) {
      // Form submissions must use the synchronous sync to ensure the input value is current.
      this._syncAll = () => { this.syncNow() }
      this.form.addEventListener("turbo:submit-start", this._syncAll)
      this.form.addEventListener("submit", this._syncAll)
      this.form.addEventListener("formdata", this._syncAll)
    }
  }

  disconnect() {
    if (this.form) {
      this.form.removeEventListener("turbo:submit-start", this._syncAll)
      this.form.removeEventListener("submit", this._syncAll)
      this.form.removeEventListener("formdata", this._syncAll)
    }
    this._teardownImageResize()
    if (this._onBlockMenuDocClick) document.removeEventListener("click", this._onBlockMenuDocClick)
    if (this._onSelectionChange) document.removeEventListener("selectionchange", this._onSelectionChange)
    if (this._onWindowResize) window.removeEventListener("resize", this._onWindowResize)
    if (this._onEditorDragOver) this.editorTarget.removeEventListener("dragover", this._onEditorDragOver)
    if (this._onDocDragOver) document.removeEventListener("dragover", this._onDocDragOver)
    if (this._autoScrollRaf) { cancelAnimationFrame(this._autoScrollRaf); this._autoScrollRaf = null }
    if (this._onEditorDrop) this.editorTarget.removeEventListener("drop", this._onEditorDrop)
    if (this._onBlockClickSelect) this.editorTarget.removeEventListener("click", this._onBlockClickSelect)
    if (this._onEnterKey) this.editorTarget.removeEventListener("keydown", this._onEnterKey)
    if (this._onKeyBlockProtect) this.editorTarget.removeEventListener("keydown", this._onKeyBlockProtect)
    if (this._onPaletteDocClick) document.removeEventListener("click", this._onPaletteDocClick)
    if (this._toolbarObserver) this._toolbarObserver.disconnect()
    if (this._raf) { cancelAnimationFrame(this._raf); this._raf = null }
    if (this._syncDebounce) { clearTimeout(this._syncDebounce); this._syncDebounce = null }
    if (this._blockMenu && this._blockMenu.parentNode === document.body) {
      document.body.removeChild(this._blockMenu)
    }
  }

  // Move menu to body (escape backdrop-filter / transform ancestors that break position:fixed)
  // and wire button clicks manually since they're outside controller scope.
  _setupBlockMenu() {
    if (!this.hasBlockMenuTarget) return
    this._blockMenu = this.blockMenuTarget
    document.body.appendChild(this._blockMenu)
    this._blockMenu.querySelectorAll("[data-block-type]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault()
        e.stopPropagation()
        this._handleBlockTypeClick(btn.dataset.blockType, e)
      })
    })
  }

  _updateToolbarState() {
    const els = this.element.querySelectorAll("[data-toolbar-state]")
    els.forEach(el => {
      const cmd = el.dataset.toolbarState
      if (cmd === "formatBlock") {
        try {
          let val = (document.queryCommandValue("formatBlock") || "").toLowerCase()
          // Some browsers return tag in angle brackets like "<h2>"
          val = val.replace(/[<>]/g, "")
          if (val === "" || val === "div") val = "p"
          if (el.value !== undefined) el.value = val
        } catch (_) {}
        return
      }
      let active = false
      try { active = document.queryCommandState(cmd) } catch (_) {}
      el.classList.toggle("bg-primary/15", active)
      el.classList.toggle("text-primary", active)
      el.classList.toggle("ring-1", active)
      el.classList.toggle("ring-primary/30", active)
    })
  }

  _setupToolbarStuck() {
    if (!this.hasToolbarTarget || !this.hasToolbarSentinelTarget) return
    this._toolbarObserver = new IntersectionObserver(([entry]) => {
      const stuck = !entry.isIntersecting
      this.toolbarTarget.classList.toggle("rounded-t-xl", !stuck)
    }, { rootMargin: "0px 0px 0px 0px", threshold: [1] })
    this._toolbarObserver.observe(this.toolbarSentinelTarget)
  }

  // Prevent Backspace/Delete from merging or removing whole content blocks.
  // Users must use the trash icon to delete a block. Typing/deleting within a
  // block still works normally.
  _setupBlockKeyboardProtect() {
    this._onKeyBlockProtect = (e) => {
      if (e.key !== "Backspace" && e.key !== "Delete") return
      const sel = window.getSelection()
      if (!sel || !sel.rangeCount) return
      const range = sel.getRangeAt(0)
      if (!this.editorTarget.contains(range.startContainer)) return

      const startBlock = this._topLevelBlockOf(range.startContainer)
      const endBlock = this._topLevelBlockOf(range.endContainer)

      // Non-collapsed selection that spans more than one top-level block —
      // never allow; would erase block boundaries.
      if (!range.collapsed) {
        if (startBlock && endBlock && startBlock !== endBlock) {
          e.preventDefault()
          return
        }
        // Within a single block — allow normal text deletion.
        return
      }

      if (!startBlock) return
      const blockRange = document.createRange()
      blockRange.selectNodeContents(startBlock)
      const atStart = range.compareBoundaryPoints(Range.START_TO_START, blockRange) <= 0
      const atEnd = range.compareBoundaryPoints(Range.END_TO_END, blockRange) >= 0

      // Backspace at the very start of a block would merge with previous block — block.
      if (e.key === "Backspace" && atStart) {
        e.preventDefault()
        return
      }
      // Delete at the very end would merge with next block — block.
      if (e.key === "Delete" && atEnd) {
        e.preventDefault()
        return
      }
    }
    this.editorTarget.addEventListener("keydown", this._onKeyBlockProtect)
  }

  _setupEnterKey() {
    this._onEnterKey = (e) => {
      if (e.key !== "Enter" || e.shiftKey) return
      // Inside <li>, default behavior creates a new list item — keep that.
      const sel = window.getSelection()
      if (!sel || !sel.rangeCount) return
      const node = sel.anchorNode
      if (!this.editorTarget.contains(node)) return
      let n = node
      while (n && n !== this.editorTarget) {
        if (n.nodeType === 1 && n.tagName === "LI") return
        n = n.parentNode
      }
      // Otherwise insert a <br> instead of new block
      e.preventDefault()
      document.execCommand("insertLineBreak")
      this.sync()
    }
    this.editorTarget.addEventListener("keydown", this._onEnterKey)
  }

  _setupBlockMenuDismiss() {
    if (!this._blockMenu) return
    this._onBlockMenuDocClick = (e) => {
      if (this._blockMenu.classList.contains("hidden")) return
      if (this._blockMenu.contains(e.target)) return
      if (this.hasInsertTriggerTarget && this.insertTriggerTarget.contains(e.target)) return
      this._blockMenu.classList.add("hidden")
    }
    document.addEventListener("click", this._onBlockMenuDocClick)
  }

  _setupStaticInsertTrigger() {
    if (!this.hasInsertTriggerTarget) return
    this.insertTriggerTarget.addEventListener("click", (e) => {
      e.preventDefault()
      e.stopPropagation()
      // Force "insert at end" by clearing active anchor
      this._activeBlock = null
      this.editorTarget.querySelectorAll("[data-active]").forEach(el => el.removeAttribute("data-active"))
      if (this._deleteBlockBtn) this._deleteBlockBtn.style.display = "none"
      if (this._dragHandle) this._dragHandle.style.display = "none"
      this._toggleBlockMenuAtTrigger(this.insertTriggerTarget)
    })
  }

  _toggleBlockMenuAtTrigger(triggerEl) {
    if (!this._blockMenu) return
    const menu = this._blockMenu
    if (!menu.classList.contains("hidden")) {
      menu.classList.add("hidden")
      return
    }
    const triggerRect = triggerEl.getBoundingClientRect()
    menu.style.visibility = "hidden"
    menu.style.left = "-9999px"
    menu.style.top = "0px"
    menu.classList.remove("hidden")
    void menu.offsetWidth

    const menuWidth = menu.offsetWidth
    const menuHeight = menu.offsetHeight
    let left = triggerRect.left + triggerRect.width / 2 - menuWidth / 2
    left = Math.max(8, Math.min(window.innerWidth - menuWidth - 8, left))
    let top = triggerRect.top - menuHeight - 10
    if (top < 8) top = triggerRect.bottom + 10
    if (top + menuHeight > window.innerHeight - 8) top = Math.max(8, triggerRect.top - menuHeight - 10)
    menu.style.left = left + "px"
    menu.style.top = top + "px"
    menu.style.visibility = ""
  }

  _setupDeleteBlockBtn() {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.setAttribute("data-delete-block-btn", "")
    btn.setAttribute("contenteditable", "false")
    btn.setAttribute("aria-label", "Delete block")
    btn.title = "Delete block"
    btn.style.cssText = "position:absolute; width:24px; height:24px; border-radius:50%; background:white; border:1px solid rgba(239,68,68,0.5); color:rgb(239,68,68); cursor:pointer; display:none; align-items:center; justify-content:center; padding:0; z-index:6; box-shadow:0 1px 4px rgba(0,0,0,0.1);"
    btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="display:block;"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>`
    this.editorTarget.appendChild(btn)
    this._deleteBlockBtn = btn

    btn.addEventListener("click", (e) => {
      e.preventDefault()
      e.stopPropagation()
      this._deleteActiveBlock()
    })
  }

  _scheduleAutoSave(immediate = false) {
    if (!this.hasAutosaveUrlValue || !this.autosaveUrlValue) return
    if (this._autoSaveDebounce) clearTimeout(this._autoSaveDebounce)
    if (immediate) {
      this._performAutoSave()
    } else {
      this._autoSaveDebounce = setTimeout(() => this._performAutoSave(), 1500)
    }
  }

  _performAutoSave() {
    if (!this.hasAutosaveUrlValue || !this.autosaveUrlValue) return
    if (this._autoSaveInFlight) {
      this._autoSavePending = true
      return
    }
    this._autoSaveInFlight = true
    this._setSaveStatus("Saving…")

    const csrf = document.querySelector('meta[name="csrf-token"]')
    const fd = new FormData()
    fd.append("newsletter[body]", this.inputTarget.value || "")
    if (this.hasFontInputTarget) fd.append("newsletter[font_family]", this.fontInputTarget.value || "")
    if (this.hasBgColorInputTarget) fd.append("newsletter[bg_color]", this.bgColorInputTarget.value || "")
    // Include the unsubscribe checkbox state so toggling it autosaves.
    const unsubCheckbox = this.element.querySelector('input[type="checkbox"][name="newsletter[show_unsubscribe_link]"]')
    if (unsubCheckbox) fd.append("newsletter[show_unsubscribe_link]", unsubCheckbox.checked ? "1" : "0")

    fetch(this.autosaveUrlValue, {
      method: "PATCH",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": csrf ? csrf.content : "",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: fd,
      credentials: "same-origin"
    }).then((res) => {
      if (res.ok || res.status === 204) {
        const t = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        this._setSaveStatus(`Saved ${t}`)
      } else {
        this._setSaveStatus("Save failed")
      }
    }).catch(() => {
      this._setSaveStatus("Save failed")
    }).finally(() => {
      this._autoSaveInFlight = false
      if (this._autoSavePending) {
        this._autoSavePending = false
        this._performAutoSave()
      }
    })
  }

  _setSaveStatus(text) {
    if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = text
  }

  _deleteActiveBlock() {
    const block = this._activeBlock
    if (!block || !this.editorTarget.contains(block) || this._isOverlay(block)) return
    if (!confirm("Delete this content block?")) return

    // Find replacement active block: previous content sibling, else next
    let replacement = block.previousElementSibling
    while (replacement && this._isOverlay(replacement)) replacement = replacement.previousElementSibling
    if (!replacement) {
      replacement = block.nextElementSibling
      while (replacement && this._isOverlay(replacement)) replacement = replacement.nextElementSibling
    }

    block.remove()
    this._activeBlock = replacement

    // Empty editor → seed placeholder
    const remaining = Array.from(this.editorTarget.children).filter(n => !this._isOverlay(n))
    if (remaining.length === 0) {
      const p = document.createElement("p")
      p.appendChild(document.createElement("br"))
      this.editorTarget.appendChild(p)
      this._activeBlock = p
    }

    this._refreshDraggables()
    this.sync()
    this._updateActiveBlock()
    this._scheduleAutoSave(true)
  }

  _handleBlockTypeClick(type, e) {
    if (this._blockMenu) this._blockMenu.classList.add("hidden")

    if (type === "image") return this.insertImage(e)
    if (type === "image_row") return this.insertImageRow(e)
    if (type === "image_text") return this.insertImageText(e, "image_text")
    if (type === "text_image") return this.insertImageText(e, "text_image")

    let node
    if (type === "headline") {
      node = document.createElement("h2")
      node.textContent = "Your headline"
    } else if (type === "paragraph") {
      node = document.createElement("p")
      node.textContent = "Write your text here…"
    } else if (type === "divider") {
      node = document.createElement("div")
      node.setAttribute("data-block-divider", "")
      node.setAttribute("contenteditable", "false")
      node.style.cssText = "padding:10px 0;"
      const hr = document.createElement("hr")
      hr.style.cssText = "border:0; border-top:1px solid #e5e7eb; margin:0;"
      node.appendChild(hr)
    } else {
      return
    }

    this._insertBlockAfterActive(node)
    this.sync()

    if (node.textContent && node.tagName !== "HR") {
      const range = document.createRange()
      range.selectNodeContents(node)
      const sel = window.getSelection()
      sel.removeAllRanges()
      sel.addRange(range)
      this.editorTarget.focus()
    }
    this._activeBlock = node
    this._updateActiveBlock()
  }

  _setupDragHandle() {
    const handle = document.createElement("div")
    handle.setAttribute("role", "button")
    handle.setAttribute("data-drag-handle", "")
    handle.setAttribute("contenteditable", "false")
    handle.setAttribute("draggable", "true")
    handle.setAttribute("unselectable", "on")
    handle.setAttribute("aria-label", "Drag to reorder")
    handle.title = "Drag to reorder"
    handle.style.cssText = "position:absolute; width:22px; height:22px; border-radius:4px; background:white; border:1px solid rgba(0,0,0,0.12); color:rgb(107,114,128); cursor:grab; display:none; align-items:center; justify-content:center; padding:0; z-index:6; box-shadow:0 1px 3px rgba(0,0,0,0.06); user-select:none; -webkit-user-select:none; -webkit-user-drag:element;"
    handle.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style="display:block; pointer-events:none;" draggable="false"><circle cx="9" cy="6" r="1.5"/><circle cx="15" cy="6" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="9" cy="18" r="1.5"/><circle cx="15" cy="18" r="1.5"/></svg>`

    // Prevent contenteditable from grabbing the mousedown and starting a text selection.
    handle.addEventListener("mousedown", (e) => {
      e.stopPropagation()
      // Don't preventDefault — that blocks native drag initiation in Chrome.
    })

    this.editorTarget.appendChild(handle)
    this._dragHandle = handle

    // Drop line indicator
    const line = document.createElement("div")
    line.setAttribute("data-drop-line", "")
    line.setAttribute("contenteditable", "false")
    line.style.cssText = "position:absolute; left:0; right:0; height:3px; background:rgb(59,130,246); border-radius:2px; pointer-events:none; display:none; z-index:7;"
    this.editorTarget.appendChild(line)
    this._dropLine = line

    handle.addEventListener("dragstart", (e) => {
      if (!this._activeBlock || this._isOverlay(this._activeBlock)) {
        e.preventDefault()
        return
      }
      // setData MUST come first — Chrome on Windows rejects drags otherwise.
      try {
        e.dataTransfer.setData("text/plain", "block")
        e.dataTransfer.effectAllowed = "move"
      } catch (_) {}
      this._draggedBlock = this._activeBlock
      this._draggedBlock.style.opacity = "0.4"
      handle.style.cursor = "grabbing"
      this._dragLastY = e.clientY
      this._startAutoScroll()
    })

    handle.addEventListener("dragend", () => {
      if (this._draggedBlock) this._draggedBlock.style.opacity = ""
      this._draggedBlock = null
      this._dropLine.style.display = "none"
      handle.style.cursor = "grab"
      this._refreshDraggables()
      this._stopAutoScroll()
    })

    // Track mouse Y at document level so auto-scroll keeps working when the
    // cursor leaves the editor's box (over the top of the page).
    this._onDocDragOver = (e) => {
      if (!this._draggedBlock) return
      this._dragLastY = e.clientY
    }
    document.addEventListener("dragover", this._onDocDragOver)

    this._onEditorDragOver = (e) => {
      if (!this._draggedBlock) return
      e.preventDefault()
      e.dataTransfer.dropEffect = "move"
      this._dragLastY = e.clientY
      const target = this._computeDropTarget(e.clientY)
      if (target) {
        const editorRect = this.editorTarget.getBoundingClientRect()
        this._dropLine.style.top = (target.y - editorRect.top - 1) + "px"
        this._dropLine.style.display = "block"
      } else {
        this._dropLine.style.display = "none"
      }
    }
    this.editorTarget.addEventListener("dragover", this._onEditorDragOver)

    this._onEditorDrop = (e) => {
      if (!this._draggedBlock) return
      e.preventDefault()
      const target = this._computeDropTarget(e.clientY)
      if (target && target.before !== this._draggedBlock && target.before !== this._draggedBlock.nextSibling) {
        this.editorTarget.insertBefore(this._draggedBlock, target.before)
        this._refreshDraggables()
        this.sync()
        this._updateActiveBlock()
      }
      this._dropLine.style.display = "none"
    }
    this.editorTarget.addEventListener("drop", this._onEditorDrop)
  }

  // Auto-scroll the window during block drag when the pointer sits near the
  // top/bottom edge of the viewport. Lets users drag a block above/below the
  // currently visible editor area.
  _startAutoScroll() {
    if (this._autoScrollRaf) return
    const tick = () => {
      if (!this._draggedBlock) { this._autoScrollRaf = null; return }
      const y = this._dragLastY
      const margin = 90
      const maxSpeed = 18
      if (typeof y === "number") {
        if (y < margin) {
          const t = (margin - y) / margin
          window.scrollBy(0, -Math.ceil(t * maxSpeed))
        } else if (y > window.innerHeight - margin) {
          const t = (y - (window.innerHeight - margin)) / margin
          window.scrollBy(0, Math.ceil(t * maxSpeed))
        }
      }
      this._autoScrollRaf = requestAnimationFrame(tick)
    }
    this._autoScrollRaf = requestAnimationFrame(tick)
  }

  _stopAutoScroll() {
    if (this._autoScrollRaf) {
      cancelAnimationFrame(this._autoScrollRaf)
      this._autoScrollRaf = null
    }
  }

  _computeDropTarget(clientY) {
    const blocks = Array.from(this.editorTarget.children).filter(n => !this._isOverlay(n))
    for (const block of blocks) {
      const rect = block.getBoundingClientRect()
      const mid = rect.top + rect.height / 2
      if (clientY < mid) {
        return { before: block, y: rect.top - 6 }
      }
    }
    const last = blocks[blocks.length - 1]
    if (last) {
      const rect = last.getBoundingClientRect()
      return { before: null, y: rect.bottom + 6 }
    }
    return null
  }

  _refreshDraggables() {
    Array.from(this.editorTarget.children).forEach(n => {
      if (this._isOverlay(n)) return
      // Block itself not draggable; drag is initiated from the handle only
      n.removeAttribute("draggable")
    })
  }

  _setupBlockClickSelection() {
    this._onBlockClickSelect = (e) => {
      if (e.target.closest("[data-resize-handle], [data-delete-btn], [data-delete-block-btn], [data-drag-handle], [data-drop-line]")) return
      const block = this._topLevelBlockOf(e.target)
      if (block) this._updateActiveBlock(block)
    }
    this.editorTarget.addEventListener("click", this._onBlockClickSelect)
  }

  _setupActiveBlockTracking() {
    const pos = getComputedStyle(this.editorTarget).position
    if (pos === "static") this.editorTarget.style.position = "relative"

    this._onSelectionChange = () => {
      this._updateActiveBlock()
      // Continuously track the latest editor selection so toolbar actions can
      // restore it after focus shifts to dropdowns/buttons.
      const sel = window.getSelection()
      if (sel && sel.rangeCount && this.editorTarget.contains(sel.anchorNode) && this.editorTarget.contains(sel.focusNode)) {
        this._savedRange = sel.getRangeAt(0).cloneRange()
        this._updateToolbarState()
      }
    }
    document.addEventListener("selectionchange", this._onSelectionChange)

    this._onWindowResize = () => this._updateActiveBlock()
    window.addEventListener("resize", this._onWindowResize)

    requestAnimationFrame(() => this._updateActiveBlock())
  }

  _topLevelBlockOf(node) {
    while (node && node !== this.editorTarget && node.parentNode !== this.editorTarget) {
      node = node.parentNode
    }
    if (!node || node === this.editorTarget) return null
    if (this._isOverlay(node)) return null
    return node
  }

  _isOverlay(node) {
    return node === this._deleteBlockBtn || node === this._dragHandle || node === this._dropLine
  }

  // Coalesces rapid-fire calls (selectionchange, etc.) into a single rAF tick
  // so we don't thrash layout on every keystroke / cursor movement.
  _updateActiveBlock(explicitBlock = null) {
    if (explicitBlock) this._pendingExplicitBlock = explicitBlock
    if (this._raf) return
    this._raf = requestAnimationFrame(() => {
      this._raf = null
      const explicit = this._pendingExplicitBlock
      this._pendingExplicitBlock = null
      this._doUpdateActiveBlock(explicit || null)
    })
  }

  _doUpdateActiveBlock(explicitBlock) {
    let active = explicitBlock

    if (!active) {
      const sel = window.getSelection()
      if (sel && sel.rangeCount && this.editorTarget.contains(sel.anchorNode)) {
        let node = sel.anchorNode
        if (node === this.editorTarget && this.editorTarget.children.length > 0) {
          const idx = Math.min(sel.anchorOffset, this.editorTarget.children.length - 1)
          node = this.editorTarget.children[idx]
        }
        active = this._topLevelBlockOf(node)
      } else if (this._activeBlock && this.editorTarget.contains(this._activeBlock)) {
        active = this._activeBlock
      }
    }

    this.editorTarget.querySelectorAll("[data-active]").forEach(el => el.removeAttribute("data-active"))

    if (!active) {
      if (this._deleteBlockBtn) this._deleteBlockBtn.style.display = "none"
      if (this._dragHandle) this._dragHandle.style.display = "none"
      this._activeBlock = null
      return
    }

    active.setAttribute("data-active", "")
    this._activeBlock = active
    const rect = active.getBoundingClientRect()
    const editorRect = this.editorTarget.getBoundingClientRect()

    if (this._dragHandle) {
      this._dragHandle.style.display = "flex"
      this._dragHandle.style.top = (rect.top - editorRect.top - 4) + "px"
      this._dragHandle.style.left = (rect.left - editorRect.left - 30) + "px"
    }
    if (this._deleteBlockBtn) {
      this._deleteBlockBtn.style.display = "flex"
      this._deleteBlockBtn.style.top = (rect.top - editorRect.top + 32) + "px"
      this._deleteBlockBtn.style.left = (rect.left - editorRect.left - 31) + "px"
    }
  }

  // Image resize: click an image to select it, drag corner handle to resize
  _setupImageResize() {
    this._onEditorClick = (e) => {
      const img = e.target.closest("img")
      if (img && this.editorTarget.contains(img)) {
        e.preventDefault()
        this._selectImage(img)
      } else if (!e.target.closest("[data-resize-handle]") && !e.target.closest("[data-delete-btn]")) {
        this._deselectImage()
      }
    }
    this.editorTarget.addEventListener("click", this._onEditorClick)

    this._onDocClick = (e) => {
      if (!this.editorTarget.contains(e.target)) this._deselectImage()
    }
    document.addEventListener("click", this._onDocClick)

    this._onKeyDown = (e) => {
      if (this._selectedImg && (e.key === "Delete" || e.key === "Backspace")) {
        e.preventDefault()
        this._selectedImg.remove()
        this._deselectImage()
        this.sync()
      }
    }
    document.addEventListener("keydown", this._onKeyDown)
  }

  _teardownImageResize() {
    if (this._onEditorClick) this.editorTarget.removeEventListener("click", this._onEditorClick)
    if (this._onDocClick) document.removeEventListener("click", this._onDocClick)
    if (this._onKeyDown) document.removeEventListener("keydown", this._onKeyDown)
    this._deselectImage()
  }

  _selectImage(img) {
    this._deselectImage()
    this._selectedImg = img

    img.style.outline = "2px solid var(--primary-color, #16a34a)"
    img.style.outlineOffset = "2px"

    const pos = getComputedStyle(this.editorTarget).position
    if (pos === "static") this.editorTarget.style.position = "relative"

    // Resize handle (bottom-right)
    const handle = document.createElement("div")
    handle.setAttribute("data-resize-handle", "")
    handle.style.cssText = `
      position: absolute; width: 10px; height: 10px;
      background: var(--primary-color, #16a34a); border: 1px solid white;
      border-radius: 0; cursor: nwse-resize; z-index: 10;
      box-shadow: 0 1px 2px rgba(0,0,0,0.3); padding: 0; margin: 0;
    `
    this._positionHandle(img, handle)
    this.editorTarget.appendChild(handle)
    this._resizeHandle = handle



    // Drag-to-resize
    let startX, startWidth

    const onMouseDown = (e) => {
      e.preventDefault()
      e.stopPropagation()
      startX = e.clientX
      startWidth = img.offsetWidth
      document.addEventListener("mousemove", onMouseMove)
      document.addEventListener("mouseup", onMouseUp)
    }

    const onMouseMove = (e) => {
      const dx = e.clientX - startX
      const newWidth = Math.max(50, startWidth + dx)
      const maxWidth = this.editorTarget.clientWidth - 20
      img.style.width = Math.min(newWidth, maxWidth) + "px"
      img.style.height = "auto"
      this._positionHandle(img, handle)
    }

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove)
      document.removeEventListener("mouseup", onMouseUp)
      this.sync()
    }

    handle.addEventListener("mousedown", onMouseDown)

    handle.addEventListener("touchstart", (e) => {
      e.preventDefault()
      e.stopPropagation()
      const touch = e.touches[0]
      startX = touch.clientX
      startWidth = img.offsetWidth

      const onTouchMove = (e) => {
        const t = e.touches[0]
        const dx = t.clientX - startX
        const newWidth = Math.max(50, startWidth + dx)
        const maxWidth = this.editorTarget.clientWidth - 20
        img.style.width = Math.min(newWidth, maxWidth) + "px"
        img.style.height = "auto"
        this._positionHandle(img, handle)
      }

      const onTouchEnd = () => {
        document.removeEventListener("touchmove", onTouchMove)
        document.removeEventListener("touchend", onTouchEnd)
        this.sync()
      }

      document.addEventListener("touchmove", onTouchMove)
      document.addEventListener("touchend", onTouchEnd)
    })
  }

  _positionHandle(img, handle) {
    const editorRect = this.editorTarget.getBoundingClientRect()
    const imgRect = img.getBoundingClientRect()
    handle.style.top = (imgRect.bottom - editorRect.top - 7) + "px"
    handle.style.left = (imgRect.right - editorRect.left - 7) + "px"
  }

  _deselectImage() {
    if (this._selectedImg) {
      this._selectedImg.style.outline = ""
      this._selectedImg.style.outlineOffset = ""
      this._selectedImg = null
    }
    if (this._resizeHandle) { this._resizeHandle.remove(); this._resizeHandle = null }
  }

  // Debounced for input events. Immediate sync available via syncNow.
  sync() {
    if (this._syncDebounce) clearTimeout(this._syncDebounce)
    this._syncDebounce = setTimeout(() => this.syncNow(), 150)
  }

  syncNow() {
    if (this._syncDebounce) { clearTimeout(this._syncDebounce); this._syncDebounce = null }

    const hadSelection = !!this._selectedImg
    const selectedImg = this._selectedImg
    if (hadSelection) {
      selectedImg.style.outline = ""
      selectedImg.style.outlineOffset = ""
    }

    const clone = this.editorTarget.cloneNode(true)
    clone.querySelectorAll("[data-resize-handle], [data-delete-btn], [data-delete-block-btn], [data-drag-handle], [data-drop-line], [data-cell-delete]").forEach(p => p.remove())
    clone.querySelectorAll("[data-active]").forEach(el => el.removeAttribute("data-active"))
    clone.querySelectorAll("[draggable]").forEach(el => el.removeAttribute("draggable"))
    clone.querySelectorAll("[contenteditable]").forEach(el => el.removeAttribute("contenteditable"))
    this.inputTarget.value = clone.innerHTML

    if (hadSelection && selectedImg) {
      selectedImg.style.outline = "2px solid var(--primary-color, #16a34a)"
      selectedImg.style.outlineOffset = "2px"
    }

    this._scheduleAutoSave()
  }

  bold(e) {
    e.preventDefault()
    document.execCommand("bold")
    this.sync()
  }

  italic(e) {
    e.preventDefault()
    document.execCommand("italic")
    this.sync()
  }

  // Toolbar button variant (reads data-tag). formatBlockSelect handles the <select> variant.
  formatBlock(e) {
    e.preventDefault()
    const tag = e.currentTarget.dataset.tag
    if (!tag) return
    this._restoreSelection()
    document.execCommand("formatBlock", false, tag)
    this.sync()
  }

  formatBlockSelect(e) {
    const tag = e.currentTarget.value
    if (!tag) return
    this._restoreSelection()
    document.execCommand("formatBlock", false, tag)

    // After changing block type, the new tag inherits any inline `font-size`
    // from the previous block — that's why a paragraph-with-custom-size can
    // suddenly stay tiny when promoted to H1 (or vice versa). Reset the
    // block-level + inner-span font-size so the new block's CSS default wins.
    const sel = window.getSelection()
    if (sel && sel.rangeCount) {
      const range = sel.getRangeAt(0)
      const blocks = range.collapsed
        ? [this._enclosingBlockFor(range.startContainer)].filter(Boolean)
        : Array.from(this._blocksInRange(range))
      blocks.forEach(b => {
        b.style.fontSize = ""
        this._stripFontSizeSpansInside(b)
      })
    }

    this.sync()
  }

  bulletList(e) {
    e.preventDefault()
    document.execCommand("insertUnorderedList")
    this.sync()
  }

  orderedList(e) {
    e.preventDefault()
    document.execCommand("insertOrderedList")
    this.sync()
  }

  alignLeft(e) { e?.preventDefault(); this._applyAlign("left") }
  alignCenter(e) { e?.preventDefault(); this._applyAlign("center") }
  alignRight(e) { e?.preventDefault(); this._applyAlign("right") }

  // Apply text-align to the enclosing block (and any selected blocks).
  // For images: image is wrapped/centered via margin auto, so we set the
  // image's margin instead so alignment actually has a visual effect.
  _applyAlign(align) {
    this._restoreSelection()
    const sel = window.getSelection()
    if (!sel.rangeCount) {
      // Fall back to last active block
      if (!this._activeBlock) return
      this._setAlignOn(this._activeBlock, align)
      this.sync()
      return
    }
    const range = sel.getRangeAt(0)
    if (!this.editorTarget.contains(range.commonAncestorContainer)) return

    // If a single image is the entire selection (or caret is on an image), align it
    if (this._selectedImg && this.editorTarget.contains(this._selectedImg)) {
      this._setImageAlign(this._selectedImg, align)
      this.sync()
      return
    }

    const blocks = range.collapsed
      ? new Set([this._enclosingBlockFor(range.startContainer)].filter(Boolean))
      : this._blocksInRange(range)
    blocks.forEach(b => this._setAlignOn(b, align))

    // Also align any images intersecting the range
    if (!range.collapsed) {
      const imgs = Array.from(this.editorTarget.querySelectorAll("img"))
        .filter(img => range.intersectsNode(img))
      imgs.forEach(img => this._setImageAlign(img, align))
    }

    this.sync()
  }

  _setAlignOn(el, align) {
    if (!el) return
    el.style.textAlign = align
    // Image-wrap divs / figures may center via margin: 0 auto on the img.
    // Update the inner img alignment too.
    el.querySelectorAll("img").forEach(img => this._setImageAlign(img, align))
  }

  _setImageAlign(img, align) {
    if (align === "center") {
      img.style.marginLeft = "auto"
      img.style.marginRight = "auto"
      img.style.display = "block"
    } else if (align === "left") {
      img.style.marginLeft = "0"
      img.style.marginRight = "auto"
      img.style.display = "block"
    } else if (align === "right") {
      img.style.marginLeft = "auto"
      img.style.marginRight = "0"
      img.style.display = "block"
    }
  }

  // Add caption beneath the currently-selected image (or active image block).
  toggleImageCaption(e) {
    e?.preventDefault()
    let img = this._selectedImg
    if (!img && this._activeBlock) {
      img = this._activeBlock.querySelector?.("img")
    }
    if (!img) return

    // Find the closest wrapper (image-row td uses its own caption; standalone image lives in a wrap div)
    const cell = img.closest("td[data-image-cell], div[data-block-image]")
    if (!cell) {
      // Wrap bare image in a div so we can attach a caption below it
      const wrap = document.createElement("div")
      wrap.setAttribute("data-block-image", "")
      wrap.style.cssText = "width:100%; margin:8px 0; text-align:center;"
      img.parentNode.insertBefore(wrap, img)
      wrap.appendChild(img)
      this._appendOrToggleCaption(wrap)
    } else {
      this._appendOrToggleCaption(cell)
    }
    this.sync()
    this._scheduleAutoSave(true)
  }

  _appendOrToggleCaption(container) {
    const existing = container.querySelector(":scope > [data-caption]")
    if (existing) {
      existing.remove()
      return null
    }
    const caption = document.createElement("p")
    caption.setAttribute("data-caption", "")
    caption.style.cssText = "margin:6px 0 0; font-size:13px; color:#6b7280; text-align:center; min-height:1em; line-height:1.4; font-style:italic;"
    caption.appendChild(document.createElement("br"))
    container.appendChild(caption)
    // Focus the caption for immediate typing
    requestAnimationFrame(() => {
      const range = document.createRange()
      range.selectNodeContents(caption)
      range.collapse(true)
      const sel = window.getSelection()
      sel.removeAllRanges()
      sel.addRange(range)
      this.editorTarget.focus()
    })
    return caption
  }

  fontSize(e) {
    const size = e.currentTarget.value
    if (!size) return

    // Restore selection if dropdown stole focus
    this._restoreSelection()

    const selection = window.getSelection()
    if (!selection.rangeCount) {
      e.currentTarget.value = ""
      return
    }
    const range = selection.getRangeAt(0)
    if (!this.editorTarget.contains(range.commonAncestorContainer)) {
      e.currentTarget.value = ""
      return
    }

    if (range.collapsed) {
      // No selection — apply size to enclosing block (p, li, h2, h3, etc.)
      const block = this._enclosingBlockFor(range.startContainer)
      if (block) {
        this._stripFontSizeSpansInside(block)
        block.style.fontSize = size + "px"
        this.sync()
      }
      e.currentTarget.value = ""
      return
    }

    // Apply size to all blocks (li, p, h2, etc.) intersecting the selection.
    // Strip prior inline font-size spans first to avoid compounding.
    this._blocksInRange(range).forEach(block => {
      this._stripFontSizeSpansInside(block)
      block.style.fontSize = size + "px"
    })

    // Also wrap inline runs (for partial selection within a single block).
    document.execCommand("fontSize", false, "7")
    this.editorTarget.querySelectorAll('font[size="7"]').forEach((el) => {
      const span = document.createElement("span")
      span.style.fontSize = size + "px"
      while (el.firstChild) span.appendChild(el.firstChild)
      el.parentNode.replaceChild(span, el)
    })

    this.sync()

    // Reset select so same size can be re-applied
    e.currentTarget.value = ""
  }

  // Unwrap any descendant <span style="font-size: ...">…</span> back into bare text.
  // Prevents stacking when fontSize is reapplied.
  _stripFontSizeSpansInside(root) {
    if (!root) return
    const spans = Array.from(root.querySelectorAll('span[style*="font-size"]'))
    spans.forEach(span => {
      span.style.fontSize = ""
      if (!span.getAttribute("style") || span.getAttribute("style").trim() === "") {
        const parent = span.parentNode
        while (span.firstChild) parent.insertBefore(span.firstChild, span)
        parent.removeChild(span)
      }
    })
  }

  _enclosingBlockFor(node) {
    const blockTags = ["LI", "P", "H1", "H2", "H3", "H4", "H5", "H6", "DIV", "BLOCKQUOTE"]
    while (node && node !== this.editorTarget) {
      if (node.nodeType === 1 && blockTags.includes(node.tagName)) return node
      node = node.parentNode
    }
    return null
  }

  _blocksInRange(range) {
    const blockTags = new Set(["LI", "P", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE"])
    const blocks = new Set()
    const startBlock = this._enclosingBlockFor(range.startContainer)
    const endBlock = this._enclosingBlockFor(range.endContainer)
    if (startBlock) blocks.add(startBlock)
    if (endBlock) blocks.add(endBlock)
    const walker = document.createTreeWalker(this.editorTarget, NodeFilter.SHOW_ELEMENT, {
      acceptNode: (node) => blockTags.has(node.tagName) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP
    })
    let n
    while ((n = walker.nextNode())) {
      if (range.intersectsNode(n)) blocks.add(n)
    }
    return blocks
  }

  fontFamily(e) {
    const fontStack = e.currentTarget.value
    if (!fontStack) return

    if (this.hasWrapperTarget) {
      this.wrapperTarget.style.fontFamily = fontStack
    }
    if (this.hasFontInputTarget) {
      this.fontInputTarget.value = fontStack
    }
  }

  newsletterBgColor(e) {
    if (e.type === "click") e.preventDefault()
    const color = e.currentTarget.dataset.color || e.currentTarget.value
    if (!color) return
    if (this.hasSurroundingsTarget) this.surroundingsTarget.style.background = color
    if (this.hasNewsletterBgSwatchTarget) this.newsletterBgSwatchTarget.style.background = color
    if (this.hasBgColorInputTarget) this.bgColorInputTarget.value = color
    this._closePalettes()
    this._scheduleAutoSave()
  }

  captureSelection() {
    const sel = window.getSelection()
    if (sel && sel.rangeCount && this.editorTarget.contains(sel.anchorNode)) {
      this._savedRange = sel.getRangeAt(0).cloneRange()
    }
  }

  _restoreSelection() {
    if (!this._savedRange) return false
    const sel = window.getSelection()
    sel.removeAllRanges()
    sel.addRange(this._savedRange)
    return true
  }

  textColor(e) {
    if (e.type === "click") e.preventDefault()
    const color = e.currentTarget.dataset.color || e.currentTarget.value
    if (!color) return
    if (!this._restoreSelection()) return
    document.execCommand("styleWithCSS", false, true)
    document.execCommand("foreColor", false, color)
    if (this.hasTextColorSwatchTarget) this.textColorSwatchTarget.style.background = color
    this._collapseSelectionToEnd()
    this._closePalettes()
    this.sync()
  }

  bgColor(e) {
    if (e.type === "click") e.preventDefault()
    const color = e.currentTarget.dataset.color || e.currentTarget.value
    if (!color) return
    if (!this._restoreSelection()) return
    document.execCommand("styleWithCSS", false, true)
    if (!document.execCommand("hiliteColor", false, color)) {
      document.execCommand("backColor", false, color)
    }
    if (this.hasBgColorSwatchTarget) this.bgColorSwatchTarget.style.background = color
    this._collapseSelectionToEnd()
    this._closePalettes()
    this.sync()
  }

  _collapseSelectionToEnd() {
    const sel = window.getSelection()
    if (sel && sel.rangeCount) sel.collapseToEnd()
    this._savedRange = null
  }

  preventBlur(e) {
    e.preventDefault()
  }

  togglePalette(e) {
    e.preventDefault()
    const key = e.currentTarget.dataset.paletteKey
    const targetName = key + "Palette"
    const allPalettes = this.element.querySelectorAll("[data-palette-name]")
    allPalettes.forEach(p => {
      if (p.dataset.paletteName !== key) p.classList.add("hidden")
    })
    const panel = this.element.querySelector(`[data-palette-name="${key}"]`)
    if (panel) panel.classList.toggle("hidden")

    if (!this._onPaletteDocClick) {
      this._onPaletteDocClick = (ev) => {
        if (ev.target.closest("[data-palette-name]")) return
        if (ev.target.closest("[data-palette-key]")) return
        this._closePalettes()
      }
      document.addEventListener("click", this._onPaletteDocClick)
    }
  }

  _closePalettes() {
    this.element.querySelectorAll("[data-palette-name]").forEach(p => p.classList.add("hidden"))
  }

  clearColor(e) {
    e.preventDefault()
    this._restoreSelection()
    const sel = window.getSelection()
    if (!sel.rangeCount) return
    const range = sel.getRangeAt(0)
    if (!this.editorTarget.contains(range.commonAncestorContainer)) return

    const stripStyles = (el) => {
      if (el.nodeType !== 1) return
      el.style.color = ""
      el.style.backgroundColor = ""
      el.style.background = ""
      if (el.tagName === "FONT") el.removeAttribute("color")
    }

    if (range.collapsed) {
      // Walk ancestors from caret up to editor
      let node = range.startContainer
      while (node && node !== this.editorTarget) {
        stripStyles(node)
        node = node.parentNode
      }
    } else {
      // Strip styles from all elements intersecting selection (and their descendants)
      const walker = document.createTreeWalker(this.editorTarget, NodeFilter.SHOW_ELEMENT)
      let n
      while ((n = walker.nextNode())) {
        if (range.intersectsNode(n)) stripStyles(n)
      }
      // Also strip on common ancestors above
      let node = range.commonAncestorContainer
      while (node && node !== this.editorTarget) {
        stripStyles(node)
        node = node.parentNode
      }
    }

    this._collapseSelectionToEnd()
    this.sync()
  }

  insertLink(e) {
    e.preventDefault()
    const url = prompt("Enter URL:")
    if (!url) return
    document.execCommand("createLink", false, url)
    this.sync()
  }

  _compressImage(file, maxWidth = 800, quality = 0.7) {
    const isPng = (file.type || "").toLowerCase().includes("png")
    return new Promise((resolve, reject) => {
      const img = new Image()
      const url = URL.createObjectURL(file)
      const cleanup = () => URL.revokeObjectURL(url)
      img.onload = () => {
        const canvas = document.createElement("canvas")
        let w = img.width
        let h = img.height
        if (w > maxWidth) {
          h = Math.round(h * (maxWidth / w))
          w = maxWidth
        }
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext("2d")
        ctx.drawImage(img, 0, 0, w, h)
        const out = isPng ? canvas.toDataURL("image/png") : canvas.toDataURL("image/jpeg", quality)
        cleanup()
        resolve(out)
      }
      img.onerror = (err) => { cleanup(); reject(err) }
      img.src = url
    })
  }

  _insertBlockAfterActive(node) {
    const editor = this.editorTarget
    // Strip empty placeholder <p><br></p> if it's the only content block
    const blocks = Array.from(editor.children).filter(n => !this._isOverlay(n))
    if (blocks.length === 1) {
      const only = blocks[0]
      if (only.tagName === "P" && only.innerHTML === "<br>") {
        editor.removeChild(only)
        if (this._activeBlock === only) this._activeBlock = null
      }
    }

    const anchor = (this._activeBlock && this._activeBlock.parentNode === editor && !this._isOverlay(this._activeBlock))
      ? this._activeBlock
      : null

    if (anchor) {
      editor.insertBefore(node, anchor.nextSibling)
    } else {
      // No active anchor: append to end (still before any trailing overlays via filter check)
      editor.appendChild(node)
    }

    this._refreshDraggables()
  }

  _buildImageRowCell(src, i, total) {
    const colWidth = (100 / total).toFixed(2) + "%"
    const padLeft = i === 0 ? 0 : 4
    const padRight = i === total - 1 ? 0 : 4
    const td = document.createElement("td")
    td.style.cssText = `position:relative; width:${colWidth}; padding:0 ${padRight}px 0 ${padLeft}px; vertical-align:top;`

    const img = document.createElement("img")
    img.src = src
    img.style.cssText = "max-width:100%; height:auto; display:block;"
    td.appendChild(img)

    const caption = document.createElement("p")
    caption.setAttribute("data-caption", "")
    caption.style.cssText = "margin:6px 0 0; font-size:13px; color:#6b7280; text-align:center; min-height:1em;"
    caption.appendChild(document.createElement("br"))
    td.appendChild(caption)

    const cellDel = document.createElement("button")
    cellDel.type = "button"
    cellDel.setAttribute("data-cell-delete", "")
    cellDel.setAttribute("contenteditable", "false")
    cellDel.title = "Remove this image"
    cellDel.style.cssText = "position:absolute; top:6px; right:8px; width:22px; height:22px; border-radius:50%; background:rgba(239,68,68,0.92); border:1px solid white; color:white; cursor:pointer; display:none; align-items:center; justify-content:center; padding:0; z-index:6; box-shadow:0 1px 3px rgba(0,0,0,0.2);"
    cellDel.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" style="display:block;"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>`
    cellDel.addEventListener("click", (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (!confirm("Remove this image from the row?")) return
      this._deleteImageRowCell(td)
    })
    td.appendChild(cellDel)

    return td
  }

  _deleteImageRowCell(td) {
    const tr = td.parentNode
    if (!tr) return
    const table = tr.closest("table")
    td.remove()
    const remaining = Array.from(tr.children)
    if (remaining.length === 0) {
      // No cells left → delete whole table block
      const wasActive = this._activeBlock === table
      table.remove()
      if (wasActive) this._activeBlock = null
      // Empty editor → seed placeholder
      const blocks = Array.from(this.editorTarget.children).filter(n => !this._isOverlay(n))
      if (blocks.length === 0) {
        const p = document.createElement("p")
        p.appendChild(document.createElement("br"))
        this.editorTarget.appendChild(p)
        this._activeBlock = p
      }
    } else {
      // Redistribute widths + paddings across remaining cells
      const colWidth = (100 / remaining.length).toFixed(2) + "%"
      remaining.forEach((c, i) => {
        const padLeft = i === 0 ? 0 : 4
        const padRight = i === remaining.length - 1 ? 0 : 4
        c.style.width = colWidth
        c.style.padding = `0 ${padRight}px 0 ${padLeft}px`
      })
    }
    this.sync()
    this._updateActiveBlock(this._activeBlock)
    this._scheduleAutoSave(true)
  }

  // Image | Text  (or  Text | Image)  side-by-side block via a 2-column table.
  // variant: "image_text" (image left) or "text_image" (text left)
  insertImageText(e, variant) {
    e?.preventDefault()
    const anchor = this._activeBlock

    const insert = (src) => {
      const table = document.createElement("table")
      table.setAttribute("role", "presentation")
      table.setAttribute("data-image-text", variant)
      table.style.cssText = "width:100%; max-width:100%; table-layout:fixed; border-collapse:collapse; margin:8px 0;"
      const tbody = document.createElement("tbody")
      const tr = document.createElement("tr")

      const imgCell = document.createElement("td")
      imgCell.style.cssText = "width:50%; vertical-align:top; padding:0 8px 0 0; text-align:center;"
      const img = document.createElement("img")
      img.src = src
      img.style.cssText = "max-width:100%; height:auto; display:block; margin:0 auto;"
      imgCell.appendChild(img)

      const textCell = document.createElement("td")
      textCell.style.cssText = "width:50%; vertical-align:top; padding:0 0 0 8px;"
      const h = document.createElement("h3")
      h.textContent = "Your heading"
      h.style.cssText = "margin:0 0 6px; padding:0; font-size:18px; font-weight:700; line-height:1.3;"
      textCell.appendChild(h)
      const p = document.createElement("p")
      p.textContent = "Write your text here…"
      p.style.cssText = "margin:0; padding:0; font-size:14px; line-height:1.5;"
      textCell.appendChild(p)

      if (variant === "image_text") {
        tr.appendChild(imgCell)
        tr.appendChild(textCell)
      } else {
        // Reverse padding for swapped order
        imgCell.style.padding = "0 0 0 8px"
        textCell.style.padding = "0 8px 0 0"
        tr.appendChild(textCell)
        tr.appendChild(imgCell)
      }
      tbody.appendChild(tr)
      table.appendChild(tbody)

      this._activeBlock = anchor
      this._insertBlockAfterActive(table)
      this._activeBlock = table
      this.sync()
      this._updateActiveBlock(table)
    }

    if (this.hasFileInputTarget) {
      this.fileInputTarget.onchange = (event) => {
        const file = event.target.files[0]
        if (!file) return
        this._compressImage(file).then(insert)
        event.target.value = ""
      }
      this.fileInputTarget.click()
    } else {
      const url = prompt("Enter image URL:")
      if (!url) return
      insert(url)
    }
  }

  insertImageRow(e) {
    e.preventDefault()
    if (!this.hasMultiFileInputTarget) return

    // Capture anchor block now (clicked-block menu / insert trigger). Selection might shift after file dialog.
    const anchor = this._activeBlock

    this.multiFileInputTarget.onchange = (event) => {
      const files = Array.from(event.target.files || []).slice(0, 4)
      if (!files.length) return

      Promise.all(files.map((f) => this._compressImage(f))).then((srcs) => {
        const table = document.createElement("table")
        table.setAttribute("role", "presentation")
        table.style.cssText = "width:100%; max-width:100%; table-layout:fixed; border-collapse:collapse; margin:8px 0;"
        const tbody = document.createElement("tbody")
        const tr = document.createElement("tr")
        const colWidth = (100 / srcs.length).toFixed(2) + "%"
        srcs.forEach((src, i) => {
          const td = this._buildImageRowCell(src, i, srcs.length)
          tr.appendChild(td)
        })
        tbody.appendChild(tr)
        table.appendChild(tbody)

        this._activeBlock = anchor
        this._insertBlockAfterActive(table)
        this._activeBlock = table
        this.sync()
        this._updateActiveBlock(table)
      })
      event.target.value = ""
    }
    this.multiFileInputTarget.click()
  }

  insertImage(e) {
    e.preventDefault()
    const anchor = this._activeBlock

    const insertImg = (src) => {
      const wrap = document.createElement("div")
      wrap.setAttribute("data-block-image", "")
      wrap.style.cssText = "width:100%; margin:8px 0; text-align:center;"
      const img = document.createElement("img")
      img.src = src
      img.style.cssText = "max-width:100%; height:auto; display:block; margin:0 auto;"
      wrap.appendChild(img)

      // Empty figcaption placeholder — user can toggle off if not needed
      const caption = document.createElement("p")
      caption.setAttribute("data-caption", "")
      caption.style.cssText = "margin:6px 0 0; font-size:13px; color:#6b7280; text-align:center; min-height:1em; line-height:1.4; font-style:italic;"
      caption.appendChild(document.createElement("br"))
      wrap.appendChild(caption)

      this._activeBlock = anchor
      this._insertBlockAfterActive(wrap)
      this._activeBlock = wrap
      this.sync()
      this._updateActiveBlock(wrap)
    }

    if (this.hasFileInputTarget) {
      this.fileInputTarget.onchange = (event) => {
        const file = event.target.files[0]
        if (!file) return
        this._compressImage(file).then(insertImg)
        event.target.value = ""
      }
      this.fileInputTarget.click()
    } else {
      const url = prompt("Enter image URL:")
      if (!url) return
      insertImg(url)
    }
  }
}
